import React from 'react';
import { Check, X } from 'lucide-react';

// Visual language for staff events, shared across every calendar/legend so they read identically
// everywhere. Lifecycle events use tinted circular icons; day-offs use expressive glyphs.
//   employed   → dark-green circle, white tick
//   reemployed → light-green circle, green tick
//   terminated → red circle, white cross
//   leave      → umbrella
//   holiday    → 🎈 balloon (festival)
//   absentee   → ban (no-entry) — an unauthorised absence
const LIFECYCLE_STYLE = {
  employed: { ring: 'bg-green-700 text-white border-green-700', Icon: Check, label: 'Employed' },
  reemployed: { ring: 'bg-green-100 text-green-700 border-green-600', Icon: Check, label: 'Re-employed' },
  terminated: { ring: 'bg-red-600 text-white border-red-600', Icon: X, label: 'Terminated' },
};

// Day-off glyphs are drawn as inline SVG so each can be shown filled (Paid / Warning state) or
// outline (Unpaid / Deduct state). Colors: leave=sky, holiday=pink, absentee=red.
const OFFDAY_STYLE = {
  leave: { color: '#0284c7', label: 'Leave' },
  holiday: { color: '#db2777', label: 'Holiday' },
  absentee: { color: '#dc2626', label: 'Absentee' },
};

export function getLifecycleStyle(type) {
  return LIFECYCLE_STYLE[type] || null;
}

/**
 * Whether a day-off icon should render solid (filled) — true when there was a financial effect:
 * leave/holiday that were PAID (is_warning), or an absentee that was DEDUCTED (!is_warning).
 * Outline means no money moved (unpaid leave/holiday, or an absentee warning).
 */
export function offDayFilled(type, isWarning) {
  return type === 'absentee' ? !isWarning : !!isWarning;
}

/** The circular lifecycle icon only. `size` is the px diameter. */
export function LifecycleIcon({ type, size = 16, title }) {
  const s = LIFECYCLE_STYLE[type];
  if (!s) return null;
  const { Icon } = s;
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full border ${s.ring} shrink-0`}
      style={{ width: size, height: size }}
      title={title || s.label}
    >
      <Icon size={Math.round(size * 0.62)} strokeWidth={3} />
    </span>
  );
}

/**
 * A day-off glyph — umbrella (leave), balloon (holiday) or ban (absentee), drawn as inline SVG.
 * `filled` = the Paid / Warning state (solid); outline = the Unpaid / Deduct state.
 */
export function OffDayIcon({ type, filled = false, size = 14, title }) {
  const s = OFFDAY_STYLE[type];
  if (!s) return null;
  const c = s.color;
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', style: { flexShrink: 0 } };
  let body = null;
  if (type === 'leave') {
    // Umbrella: filled canopy vs outline canopy.
    body = (<>
      <path d="M12 2.5C6.8 2.5 2.6 6.4 2.2 11.3a.5.5 0 0 0 .6.5H21.2a.5.5 0 0 0 .6-.5C21.4 6.4 17.2 2.5 12 2.5Z" fill={filled ? c : 'none'} stroke={c} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M12 11.8V19a2.2 2.2 0 0 1-4.4 0" stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 2.5V1" stroke={c} strokeWidth="1.6" strokeLinecap="round" />
    </>);
  } else if (type === 'holiday') {
    // Balloon: filled vs outline.
    body = (<>
      <ellipse cx="12" cy="9" rx="6" ry="7" fill={filled ? c : 'none'} stroke={c} strokeWidth="1.6" />
      <path d="M12 16v3" stroke={c} strokeWidth="1.6" strokeLinecap="round" />
      <path d="M10.7 16.2 12 17.6l1.3-1.4" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </>);
  } else if (type === 'absentee') {
    // Ban: outline circle + colored slash; filled circle + white slash.
    body = (<>
      <circle cx="12" cy="12" r="9.2" fill={filled ? c : 'none'} stroke={c} strokeWidth="1.8" />
      <line x1="5.5" y1="5.5" x2="18.5" y2="18.5" stroke={filled ? '#ffffff' : c} strokeWidth="1.8" strokeLinecap="round" />
    </>);
  }
  return <svg {...common}><title>{title || s.label}</title>{body}</svg>;
}

/**
 * Icon (lifecycle OR day-off) + optional name, for use inside a calendar day cell.
 * `filled` applies to day-offs: true = Paid / Warning (solid), false = Unpaid / Deduct (outline).
 */
export function LifecycleBadge({ type, name, filled = false, size = 14 }) {
  const isLifecycle = !!LIFECYCLE_STYLE[type];
  const isOffDay = !!OFFDAY_STYLE[type];
  if (!isLifecycle && !isOffDay) return null;
  const label = (LIFECYCLE_STYLE[type] || OFFDAY_STYLE[type]).label;
  return (
    <div className="flex items-center gap-1 leading-tight truncate" title={`${name || ''} — ${label}`.trim()}>
      {isLifecycle ? <LifecycleIcon type={type} size={size} /> : <OffDayIcon type={type} filled={filled} size={size} />}
      {name != null && <span className="text-[9px] text-gray-600 truncate">{name}</span>}
    </div>
  );
}

/** Legend entries for lifecycle events (employed / re-employed / terminated). */
export function LifecycleLegend() {
  return (
    <>
      {['employed', 'reemployed', 'terminated'].map((t) => (
        <span key={t} className="inline-flex items-center gap-1">
          <LifecycleIcon type={t} size={14} /> {LIFECYCLE_STYLE[t].label}
        </span>
      ))}
    </>
  );
}

/** Legend entries for day-offs — each shown outline (Unpaid/Deduct) then solid (Paid/Warning). */
export function OffDayLegend() {
  return (
    <>
      {['leave', 'holiday', 'absentee'].map((t) => (
        <span key={t} className="inline-flex items-center gap-1">
          <OffDayIcon type={t} filled={false} size={14} />
          <OffDayIcon type={t} filled size={14} />
          {OFFDAY_STYLE[t].label}
        </span>
      ))}
      <span className="text-gray-400">(outline = Unpaid / Warning — no financial effect · solid = Paid / Deduct — financial effect)</span>
    </>
  );
}

/** Both lifecycle and day-off legends — the full staff-event key. */
export function EventLegend() {
  return (<><LifecycleLegend /><OffDayLegend /></>);
}

export default LifecycleBadge;
